import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiMapPin, FiCalendar, FiAward } from 'react-icons/fi';
import { sportTypeAPI, eventAPI, trainerAPI, gymAPI, achievementAPI } from '../../api/services';
import { formatCurrency } from '../../utils/formatters';
import { useTranslation } from 'react-i18next';
import TrainerDetailsModal from '../../components/public/TrainerDetailsModal';

const UNSPLASH = {
  football: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=400&q=80',
  basketball: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=400&q=80',
  swimming: 'https://images.unsplash.com/photo-1530549387789-4c1017266635?w=400&q=80',
  boxing: 'https://images.unsplash.com/photo-1549719386-74dfcbf7dbed?w=400&q=80',
  tennis: 'https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=400&q=80',
  running: 'https://images.unsplash.com/photo-1461896836934-bd45ba7b5462?w=400&q=80',
  gym1: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400&q=80',
  gym2: 'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=400&q=80',
  trainer1: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400&q=80',
  trainer2: 'https://images.unsplash.com/photo-1581009146145-b5ef050c2e1e?w=400&q=80',
  event1: 'https://images.unsplash.com/photo-1461896836934-bd45ba7b5462?w=400&q=80',
  event2: 'https://images.unsplash.com/photo-1587280501635-68a0e82cd5ff?w=400&q=80',
};

const sportImages = [UNSPLASH.football, UNSPLASH.basketball, UNSPLASH.swimming, UNSPLASH.boxing, UNSPLASH.tennis, UNSPLASH.running];
const gymImages = [UNSPLASH.gym1, UNSPLASH.gym2, UNSPLASH.football, UNSPLASH.basketball];
const trainerImages = [UNSPLASH.trainer1, UNSPLASH.trainer2, UNSPLASH.boxing, UNSPLASH.running];
const eventImages = [UNSPLASH.event1, UNSPLASH.event2, UNSPLASH.swimming, UNSPLASH.tennis];

const HomePage = () => {
  const [sportTypes, setSportTypes] = useState([]);
  const [events, setEvents] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [gyms, setGyms] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    sportTypeAPI.getAll().then(r => setSportTypes(r.data?.data || [])).catch(() => {});
    eventAPI.getAll().then(r => setEvents(r.data?.data || [])).catch(() => {});
    trainerAPI.getAll().then(r => setTrainers(r.data?.data || [])).catch(() => {});
    gymAPI.getAll().then(r => setGyms(r.data?.data || [])).catch(() => {});
    achievementAPI.getAll().then(r => setAchievements(r.data?.data || [])).catch(() => {});
  }, []);

  return (
    <>
      {selectedTrainer && (
        <TrainerDetailsModal 
          trainer={selectedTrainer} 
          achievements={achievements} 
          onClose={() => setSelectedTrainer(null)} 
        />
      )}
      {/* HERO */}
      <section className="hero">
        <div className="container">
          <div className="hero-inner">
            <div className="hero-content">
              <div className="hero-badge">
                <FiAward /> {t('hero.badge')}
              </div>
              <h1 className="hero-title">
                {t('hero.title')}
              </h1>
              <p className="hero-desc">
                {t('hero.desc')}
              </p>
              <div className="hero-actions">
                <Link to="/gyms" className="btn btn-primary btn-lg">
                  {t('hero.explore_gyms')} <FiArrowRight />
                </Link>
                <Link to="/events" className="btn btn-outline btn-lg">
                  {t('hero.view_events')}
                </Link>
              </div>
              <div className="hero-stats">
                <div className="hero-stat">
                  <h3>50+</h3>
                  <p>{t('hero.stat_venues')}</p>
                </div>
                <div className="hero-stat">
                  <h3>100+</h3>
                  <p>{t('hero.stat_trainers')}</p>
                </div>
                <div className="hero-stat">
                  <h3>200+</h3>
                  <p>{t('hero.stat_events')}</p>
                </div>
              </div>
            </div>
            <div>
              <img src="/illustrations/hero.png" alt="Sports" className="hero-illustration" />
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE OFFER — Sticker Section */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">{t('home.offer_label')}</span>
            <h2 className="section-title">{t('home.offer_title')}</h2>
            <p className="section-subtitle">{t('home.offer_subtitle')}</p>
          </div>
          <div className="sticker-section">
            <div className="sticker-card">
              <img src="/illustrations/gym.png" alt="Find Gyms" className="sticker-img" />
              <h3>{t('home.find_gym_title')}</h3>
              <p>{t('home.find_gym_desc')}</p>
            </div>
            <div className="sticker-card">
              <img src="/illustrations/trainer.png" alt="Pro Trainers" className="sticker-img" />
              <h3>{t('home.pro_trainers_title')}</h3>
              <p>{t('home.pro_trainers_desc')}</p>
            </div>
            <div className="sticker-card">
              <img src="/illustrations/events.png" alt="Sports Events" className="sticker-img" />
              <h3>{t('home.join_events_title')}</h3>
              <p>{t('home.join_events_desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* SPORT TYPES */}
      <section className="section" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="section-header">
            <span className="section-label">{t('home.categories_label')}</span>
            <h2 className="section-title">{t('home.categories_title')}</h2>
            <p className="section-subtitle">{t('home.categories_subtitle')}</p>
          </div>
          <div className="grid-3">
            {(sportTypes.length > 0 ? sportTypes : [{name:'Football'}, {name:'Basketball'}, {name:'Swimming'}, {name:'Boxing'}, {name:'Tennis'}, {name:'Running'}]).slice(0, 6).map((sport, i) => (
              <div key={sport.id || i} className="card">
                <img src={sport.mediaUrl || sportImages[i % sportImages.length]} alt={sport.name} className="card-img" />
                <div className="card-body">
                  <h3 style={{ fontSize: '1.05rem', fontWeight: 700 }}>{sport.name}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EVENTS */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">{t('home.upcoming_label')}</span>
            <h2 className="section-title">{t('home.upcoming_title')}</h2>
            <p className="section-subtitle">{t('home.upcoming_subtitle')}</p>
          </div>
          <div className="grid-3">
            {(events.length > 0 ? events : [{title:'Marathon 2026', description:'Annual city marathon', location:'Tashkent', price: 0}, {title:'Boxing Championship', description:'Regional boxing tournament', location:'Samarkand', price: 50000}, {title:'Tennis Open', description:'Open tennis tournament for all levels', location:'Bukhara', price: 30000}]).slice(0, 3).map((event, i) => (
              <div key={event.id || i} className="card event-card" style={{ position: 'relative' }}>
                <div style={{ position: 'relative' }}>
                  <img src={event.mediaUrl || eventImages[i % eventImages.length]} alt={event.title} className="card-img" />
                  {event.date && (
                    <span className="event-date">
                      <FiCalendar style={{ marginRight: 4 }} />
                      {event.date.replace('T', ' ').substring(0, 16)}
                    </span>
                  )}
                </div>
                <div className="card-body">
                  <h3 style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: 6 }}>{event.title}</h3>
                  <div className="tooltip-container">
                    <p className="line-clamp-2" style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: 10 }}>
                      {event.description}
                    </p>
                    {event.description?.length > 60 && (
                      <span className="tooltip-text">{event.description}</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 4 }}><FiMapPin />{event.location}</span>
                    {event.price != null && <span className="event-price">{event.price === 0 ? t('events.free') : formatCurrency(event.price)}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <Link to="/events" className="btn btn-outline">{t('home.view_all_events')} <FiArrowRight /></Link>
          </div>
        </div>
      </section>

      {/* TRAINERS */}
      <section className="section" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="section-header">
            <span className="section-label">{t('home.professionals_label')}</span>
            <h2 className="section-title">{t('home.professionals_title')}</h2>
            <p className="section-subtitle">{t('home.professionals_subtitle')}</p>
          </div>
          <div className="grid-4">
            {(trainers.length > 0 ? trainers : [{fullName:'Alisher Karimov', position:'Boxing Coach'}, {fullName:'Dilshod Rakhimov', position:'Football Trainer'}, {fullName:'Sardor Umarov', position:'Swimming Coach'}, {fullName:'Jamshid Tursunov', position:'Fitness Expert'}]).slice(0, 4).map((trainer, i) => {
              const trainerAchievements = achievements.filter(a => a.trainerId === trainer.id);
              
              return (
                <div key={trainer.id || i} className="card trainer-card" onClick={() => setSelectedTrainer(trainer)} style={{ cursor: 'pointer' }}>
                  <img src={trainer.mediaUrl || trainerImages[i % trainerImages.length]} alt={trainer.fullName} className="card-img" />
                  <div className="card-body trainer-info">
                    <h3>{trainer.fullName}</h3>
                    <p className="position">{trainer.position}</p>
                    
                    {trainerAchievements.length > 0 && (
                      <div className="trainer-achievement" style={{ padding: '4px 8px', fontSize: '0.72rem', marginTop: 8, marginBottom: 0 }}>
                        <FiAward style={{ flexShrink: 0 }} />
                        <span className="line-clamp-1">{trainerAchievements[0].name}</span>
                        {trainerAchievements.length > 1 && <span style={{ opacity: 0.7, marginLeft: 2 }}>+{trainerAchievements.length - 1}</span>}
                      </div>
                    )}

                    {trainer.age && <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: 8 }}>{t('trainers_page.age')}: {trainer.age}</p>}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <Link to="/trainers" className="btn btn-outline">{t('home.all_trainers')} <FiArrowRight /></Link>
          </div>
        </div>
      </section>

      {/* GYMS */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">{t('home.venues_label')}</span>
            <h2 className="section-title">{t('home.venues_title')}</h2>
            <p className="section-subtitle">{t('home.venues_subtitle')}</p>
          </div>
          <div className="grid-3">
            {(gyms.length > 0 ? gyms : [{name:'Champion Gym',location:'Tashkent',sportTypeName:'Boxing'}, {name:'Aqua Center',location:'Samarkand',sportTypeName:'Swimming'}, {name:'Elite Fitness',location:'Bukhara',sportTypeName:'Fitness'}]).slice(0, 3).map((gym, i) => (
              <div key={gym.id || i} className="card gym-card">
                <img src={gym.mediaUrl || gymImages[i % gymImages.length]} alt={gym.name} className="card-img" />
                <div className="card-body">
                  <h3 style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: 5 }}>{gym.name}</h3>
                  {gym.sportTypeName && <span className="gym-badge">{gym.sportTypeName}</span>}
                  <div className="gym-meta">
                    <FiMapPin /> {gym.location}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <Link to="/gyms" className="btn btn-outline">{t('home.view_all_gyms')} <FiArrowRight /></Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section" style={{ background: 'var(--bg-secondary)', textAlign: 'center' }}>
        <div className="container">
          <span className="section-label">{t('home.cta_label')}</span>
          <h2 className="section-title" style={{ maxWidth: 480, margin: '0 auto 14px' }}>
            {t('home.cta_title')}
          </h2>
          <p className="section-subtitle" style={{ marginBottom: 32 }}>
            {t('home.cta_subtitle')}
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 14 }}>
            <Link to="/contact" className="btn btn-primary btn-lg">{t('contact.title')}</Link>
            <Link to="/events" className="btn btn-outline btn-lg">{t('hero.view_events')}</Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
